/**
 * Admin → Integrations → Marketing → Google Business.
 *
 * Connecting lives HERE, not on the Marketing page, for the same reason the
 * Zernio API key does: connecting an account is an administrative act done once
 * by whoever sets the practice up, while the Marketing section is where the
 * people who use it every day read reviews and figures. Splitting them keeps the
 * daily screens free of setup they will never touch again.
 *
 * It sits ABOVE the Zernio card deliberately, even though it depends on it —
 * Google Business is the thing a practice recognises and comes looking for;
 * Zernio is the plumbing underneath.
 *
 * SINCE 2026-09 the two are gated separately (`google business` vs `zernio`),
 * so this card can be the only one an account is entitled to. The dependency
 * did not go away: the key still carries every call, which is why the
 * !zernioConfigured state explains the missing key instead of hiding the card.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the connection state, account and
 * linked locations show, with Go to reviews; there is no Connect, Reconnect,
 * Connect another location, Disconnect, location remove or picker, and no
 * setup instructions. Nothing says why.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, Building2, CheckCircle, Loader2, MapPin, Plug, Search, Unplug, X,
} from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import {
  disconnectGoogleBusiness,
  getGoogleBusinessConnectUrl,
  getGoogleBusinessStatus,
  googleConnectError,
  listGoogleBusinessLocations,
  selectGoogleBusinessLocation,
  unlinkGoogleBusinessLocation,
  type GoogleBusinessLocation,
  type GoogleBusinessStatus,
} from '@/services/googleBusiness.service';

/**
 * Where Google returns to: the page the admin is standing on, not a fixed path.
 *
 * This screen answers to TWO urls — `/admin/marketing-integrations` (the
 * module-gated entry) and `/admin/marketing` (the plain alias) — so any single
 * hardcoded path sends half the arrivals somewhere they did not start. Reading
 * the live pathname also stops the callback drifting out of step with the route
 * table: the constant this replaced named the BREADCRUMB ("Admin → Integrations
 * → Marketing") rather than the route, matched nothing in AdminPage, and so
 * dropped the user on the admin default page after connecting — with the
 * pendingDataToken still on a URL no component was listening to, which loses the
 * location picker entirely.
 *
 * Absolute and same-origin, which the API re-checks (isAllowedRedirect). The
 * query string is deliberately dropped: the callback appends its own params, and
 * the effect above strips them again once read.
 */
const callbackUrl = () => `${window.location.origin}${window.location.pathname}`;

export function GoogleBusinessIntegrationCard() {
  const locked = useAdminPageLevel() === 'locked';
  const [status, setStatus] = useState<GoogleBusinessStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  /**
   * Whether the location picker is open.
   *
   * A boolean rather than a token: the grant now lives server-side the moment
   * the callback returns, so picking a location is an ordinary authenticated
   * call. Nothing sensitive crosses the browser, and there is no single-use
   * token that a refresh could spend.
   */
  const [picking, setPicking] = useState(false);
  const [locations, setLocations] = useState<GoogleBusinessLocation[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [noGoogleLocations, setNoGoogleLocations] = useState(false);
  const [search, setSearch] = useState('');
  const [listing, setListing] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await getGoogleBusinessStatus());
    } catch (e) {
      console.error('[GoogleBusiness] status could not be loaded:', getErrorMessage(e, 'unknown error'), e);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loadLocations = useCallback(async (term: string) => {
    setListing(true);
    setNoGoogleLocations(false);
    try {
      const res = await listGoogleBusinessLocations(term);
      setLocations(res.locations);
      setHasMore(res.hasMore);
      // An empty list with this reason is a real answer, not a failure: the
      // Google account that consented administers no listings. Saying so is the
      // difference between a fixable problem and an apparently broken page.
      setNoGoogleLocations(res.reason === 'no_google_locations');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Your Google locations could not be loaded.'));
    } finally {
      setListing(false);
    }
  }, []);

  /**
   * Read the OAuth result off the URL, then strip it.
   *
   * Cleared immediately so a refresh or a Back cannot re-trigger the picker
   * over a flow that already finished.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('gb');
    if (!outcome) return;

    if (outcome === 'error') {
      toast.error(googleConnectError(params.get('reason')));
    } else if (outcome === 'connected') {
      toast.success('Google account connected');
      void refresh();
      if (params.get('step') === 'select_location' && !locked) {
        setPicking(true);
        void loadLocations('');
      }
    }

    ['gb', 'reason', 'step'].forEach((k) => params.delete(k));
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [refresh, loadLocations, locked]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { authUrl } = await getGoogleBusinessConnectUrl(callbackUrl());
      if (!authUrl) throw new Error('no authUrl');
      window.location.assign(authUrl);
    } catch (e) {
      toast.error(getErrorMessage(e, 'The Google connection could not be started.'));
      setConnecting(false);
    }
  };

  const openPicker = () => {
    setPicking(true);
    setSearch('');
    void loadLocations('');
  };

  const choose = async (loc: GoogleBusinessLocation) => {
    setSaving(loc.id);
    try {
      const res = await selectGoogleBusinessLocation({
        locationId: loc.id,
        // Google's account resource name. Required: reviews are addressed as
        // accounts/{a}/locations/{l}, so a location alone is not resolvable.
        googleAccountName: loc.accountId,
        name: loc.name,
        address: loc.address,
        storeCode: loc.storeCode,
      });
      toast.success(`${loc.name} connected`);
      setPicking(false);
      setStatus((prev) => (prev ? { ...prev, connected: true, locations: res.locations } : prev));
    } catch (e) {
      toast.error(getErrorMessage(e, 'That location could not be connected.'));
    } finally {
      setSaving(null);
    }
  };

  const unlink = async (locationId: string, name: string) => {
    try {
      const res = await unlinkGoogleBusinessLocation(locationId);
      setStatus((prev) => (prev ? { ...prev, locations: res.locations } : prev));
      toast.success(`${name} removed`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'That location could not be removed.'));
    }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectGoogleBusiness();
      toast.success('Google account disconnected');
      setPicking(false);
      await refresh();
    } catch (e) {
      toast.error(getErrorMessage(e, 'The account could not be disconnected.'));
    } finally {
      setDisconnecting(false);
    }
  };

  const connected = status?.connected ?? false;
  const linked = status?.locations ?? [];
  const needsReconnect = status?.needsReconnect ?? false;
  // Deployment-level: no OAuth client, so connecting cannot work for anyone.
  const notConfigured = status !== null && !status.googleConfigured;

  return (
    <Card className="space-y-0">
      <CardHeader className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg grid place-items-center bg-blue-50 text-blue-600">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <div className="font-semibold text-slate-800">Google Business</div>
            <div className="mt-0.5">
              {loading ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
                  Checking&hellip;
                </span>
              ) : notConfigured ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700">
                  <AlertTriangle className="h-3 w-3" /> Not set up on this environment
                </span>
              ) : needsReconnect ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700">
                  <AlertTriangle className="h-3 w-3" /> Reconnect needed
                </span>
              ) : connected ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700">
                  <CheckCircle className="h-3 w-3" />
                  {linked.length} location{linked.length === 1 ? '' : 's'} connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
                  <Plug className="h-3 w-3" /> Not connected
                </span>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardBody className="space-y-4">
        <p className="text-sm text-slate-500">
          {!locked && (
            <>
              Connect the Google account that manages this practice&rsquo;s Business Profile, then
              choose which locations to link.{' '}
            </>
          )}
          Reviews and performance appear under Marketing &rarr; Google Business.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Spinner className="h-4 w-4" /> Loading&hellip;
          </div>
        ) : notConfigured ? (
          /* Stated rather than offering a button that cannot work. This is an
             operator problem, not something the practice can fix. */
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Google Business has no sign-in credentials configured on this environment yet, so
            connecting is not possible. This is a deployment setting, not something to retry.
          </p>
        ) : picking && !locked ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-slate-800">Choose which locations to connect</p>
              <Button variant="secondary" size="sm" onClick={() => setPicking(false)}>
                Cancel
              </Button>
            </div>

            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void loadLocations(search);
              }}
            >
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by business name"
                  className="pl-8"
                />
              </div>
              <Button type="submit" variant="secondary">Search</Button>
            </form>

            {/* Not pagination — the list is bounded and the answer is a search. */}
            {hasMore && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                This Google account has more locations than can be listed at once. Search by name to
                find the one you want.
              </p>
            )}

            <div className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {listing ? (
                <p className="px-3 py-6 text-center text-sm text-slate-500">Loading locations&hellip;</p>
              ) : noGoogleLocations ? (
                <p className="px-3 py-6 text-center text-sm text-slate-500">
                  That Google account manages no business locations. Disconnect and sign in with the
                  account that owns the Business Profile, or ask its owner to add you as a Manager.
                </p>
              ) : locations.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-slate-500">No locations matched.</p>
              ) : (
                locations.map((loc) => (
                  <div key={loc.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">{loc.name}</p>
                      <p className="truncate text-xs text-slate-500">
                        {[loc.address, loc.category, loc.storeCode].filter(Boolean).join(' \u00b7 ')}
                      </p>
                    </div>
                    <Button size="sm" disabled={saving !== null} onClick={() => void choose(loc)}>
                      {saving === loc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connect'}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {needsReconnect && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Google is no longer accepting this connection &mdash; the permission was withdrawn, or
                the Google user lost access to the listing. Reviews cannot be read or answered until
                someone reconnects.
              </p>
            )}

            {status?.connectedAs && (
              <p className="text-xs text-slate-500">Connected as {status.connectedAs}</p>
            )}

            {linked.length > 0 && (
              <div className="divide-y divide-slate-100 rounded-md border border-slate-200">
                {linked.map((loc) => (
                  <div key={loc.id} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700">
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <span className="min-w-0 flex-1 truncate">{loc.name}</span>
                    {!locked && (
                      <button
                        type="button"
                        onClick={() => void unlink(loc.id, loc.name)}
                        className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                        aria-label={`Remove ${loc.name}`}
                        title="Remove this location"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              {!locked && (
              <Button
                onClick={connected && !needsReconnect ? openPicker : connect}
                disabled={connecting}
                leftIcon={connecting ? undefined : <Building2 className="h-4 w-4" />}
              >
                {connecting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : needsReconnect ? (
                  'Reconnect Google account'
                ) : connected ? (
                  'Connect another location'
                ) : (
                  'Connect Google account'
                )}
              </Button>
              )}

              {connected && (
                <Link to="/marketing/google-business/reviews">
                  <Button variant="secondary" rightIcon={<ArrowRight className="h-4 w-4" />}>
                    Go to reviews
                  </Button>
                </Link>
              )}

              {connected && !locked && (
                <Button
                  variant="secondary"
                  onClick={() => void disconnect()}
                  disabled={disconnecting}
                  leftIcon={<Unplug className="h-4 w-4" />}
                >
                  {disconnecting ? 'Disconnecting\u2026' : 'Disconnect'}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
